import {
  MenuItem,
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { useFetchData } from "../../../components/hooks/useFetchData";
import { Visibility } from "@mui/icons-material";

const interviewdata = {
  interviewuuid: "",
  interviewdate: "",
  interviewtime: "",
  interviewername: "",
  interviewstatus: "Interview Scheduled", // Default to 'Scheduled'
  notes: "",
};

export const Interview = ({ isLoading, lead, getCandidateLifecyclEevents }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { fetchData } = useFetchData();
  const [interviewList, setInterviewList] = useState([]);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      candidateid: lead?.id || "",
      interviewuuid: interviewdata.interviewuuid,
      interviewdate: interviewdata?.interviewdate
        ? dayjs(interviewdata.interviewdate)
        : null,
      interviewtime: interviewdata.interviewtime || null,
      interviewername: interviewdata.interviewername || "",
      interviewstatus: interviewdata.interviewstatus || "",
      notes: interviewdata.notes || "",
    },
  });

  const statusList = [
    { id: "Interview Scheduled", label: "Interview Scheduled" },
    { id: "Interview Rejected", label: "Interview Rejected" },
    { id: "Interview Cleared", label: "Interview Cleared" },
  ];

  const onSubmit = async (data) => {
    const { interviewuuid, ...requiredFields } = data;

    // Check if any of the required fields are empty
    if (Object.values(requiredFields).some((field) => !field)) {
      enqueueSnackbar("All fields are required", { variant: "error" });
      return;
    } else {
      // Convert interviewdate to 'YYYY-MM-DD' format
      const formattedDate = dayjs(data.interviewdate).format("YYYY-MM-DD");

      // Convert interviewtime to 24-hour format ('HH:mm')
      const formattedTime = dayjs(data.interviewtime).format("HH:mm");

      const body = {
        ...data,
        interviewdate: formattedDate,
        interviewtime: formattedTime,
      };
      const result = await fetchData("manageinterview", {
        method: "post",
        body: body,
      });
      if (result?.code === 200) {
        // Reset the form after successful submission
        reset({
          candidateid: lead?.id || "",
          interviewuuid: "",
          interviewdate: null,
          interviewtime: null,
          interviewername: "",
          interviewstatus: "Interview Scheduled", // Reset to default 'Scheduled'
          notes: "",
        });
        getAllInterviews();
        getCandidateLifecyclEevents();
        enqueueSnackbar(result.message || "Successful!", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message || "Something went wrong!", {
          variant: "error",
        });
      }
    }
  };

  const getAllInterviews = async () => {
    const body = {
      candidateid: lead?.id,
    };
    const result = await fetchData("getinterviews", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setInterviewList(result.data);
    }
  };

  const editInterview = async (data) => {
    const combinedDateTime = dayjs(
      `${data.interviewdate} ${data.interviewtime}`,
      "YYYY-MM-DD HH:mm"
    );
    const formattedDate = dayjs(data.interviewdate, "YYYY-MM-DD");
    const formattedTime = dayjs(data.interviewtime, "HH:mm");
    reset({
      candidateid: lead?.id || "",
      interviewuuid: data.interviewuuid,
      interviewdate: formattedDate.isValid() ? formattedDate : null,
      interviewtime: formattedTime.isValid() ? formattedTime : null,
      interviewername: data.interviewername,
      interviewstatus: data.interviewstatus,
      notes: data.notes,
    });
  };

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getAllInterviews();
    };
    fetchDataAsync();
  }, []);

  const inputStyle = {
    "& .MuiInputBase-input": { m: 0, p: "4px 8px", width: "100%" },
    "& .MuiOutlinedInput-root": { borderRadius: "12px", height: 44 },
    my: 1,
    width: "23%",
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      px={2}
      flex={1}
      sx={{
        paddingBottom: "11px",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6">Schedule an Interview</Typography>

        <Stack display="flex" flexWrap="wrap" direction="row" gap={1}>
          {/* Interview Date */}
          <Controller
            name="interviewdate"
            control={control}
            rules={{ required: "Interview date is required" }}
            render={({ field }) => (
              <DatePicker
                {...field}
                format="LL"
                label="Interview date"
                sx={inputStyle}
                error={!!errors.interviewdate}
                helperText={errors.interviewdate?.message}
              />
            )}
          />
          {/* Interview Time */}
          <Controller
            name="interviewtime"
            control={control}
            rules={{ required: "Interview time is required" }}
            render={({ field }) => (
              <TimePicker
                {...field}
                ampm
                label="Interview time"
                sx={inputStyle}
                error={!!errors.interviewtime}
                helperText={errors.interviewtime?.message}
              />
            )}
          />
          {/* Interview Status */}
          <TextField
            select
            variant="outlined"
            {...register("interviewstatus", { required: "Status is required" })}
            error={!!errors.interviewstatus}
            value={watch("interviewstatus")}
            helperText={errors.interviewstatus?.message}
            sx={inputStyle}
          >
            {statusList.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {/* Interviewer Name */}
          <TextField
            placeholder="Interviewer"
            variant="outlined"
            {...register("interviewername", {
              required: "Interviewer name is required",
            })}
            error={!!errors.interviewername}
            helperText={errors.interviewername?.message}
            sx={inputStyle}
          />
          {/* Notes (Multiline Text Field) */}
          <TextField
            placeholder="Add notes"
            multiline
            rows={4}
            {...register("notes", { required: "Notes are required" })}
            error={!!errors.notes}
            helperText={errors.notes?.message}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              my: 2,
              bgcolor: "transparent",
              width: "96%",
            }}
          />
        </Stack>

        <Button
          variant="contained"
          type="submit"
          className="ly-submit-btn"
          sx={{ marginTop: 2 }}
        >
          Submit
        </Button>

        {/* Table for Interviews */}
        <TableContainer sx={{ mt: 2, maxHeight: 300, overflowY: "auto" }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "#3A80F7",
                }}
              >
                <TableCell sx={{ color: "white", borderTopLeftRadius: 8 }}>
                  Date
                </TableCell>
                <TableCell sx={{ color: "white" }}>Status</TableCell>
                <TableCell sx={{ color: "white", borderTopRightRadius: 8 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {interviewList.length > 0 ? (
                interviewList.map((li) => (
                  <TableRow key={li.id}>
                    <TableCell>
                      {li?.interviewdate
                        ? dayjs(li.interviewdate).format("LL")
                        : ""}
                    </TableCell>
                    <TableCell>{li.interviewstatus}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => editInterview(li)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No interviews found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </form>
    </Box>
  );
};
